import { useTranslation } from 'next-i18next'
import Image from 'next/image'
import React from 'react'
import { ON_DEMAND_STEP_IMAGES } from 'src/utils/onDemand'

/**
 * 4-step process: Agenda → Recolección → Almacenaje → Entrega.
 *
 * Photo-led: each step carries its own shot from `ON_DEMAND_STEP_IMAGES`
 * (truck on its way → crew loading at a home → van in a secure garage → box
 * handed back), so the section shows the service instead of describing it.
 * Mobile: compact rows (96px thumb + copy) so four steps don't cost four
 * screens of scroll. Desktop: four columns, landscape photo on top.
 *
 * The photos are decorative (`alt=""`) — the step title beside each one
 * already says what it shows. `#como-funciona` is the hero's
 * "¿Cómo funciona?" anchor target.
 */

const STEP_KEYS = ['STEP_1', 'STEP_2', 'STEP_3', 'STEP_4'] as const

const FourStepsProcess = () => {
  const { t } = useTranslation('page_almacenamiento_a_domicilio')

  return (
    <section
      id="como-funciona"
      aria-labelledby="process-heading"
      className="scroll-mt-[calc(var(--nav-h)+16px)] border-t border-ink-100/15 bg-cream-300 py-16 md:py-24"
    >
      <div className="mx-auto max-w-7xl px-5 md:px-10">
        <h2
          id="process-heading"
          className="max-w-3xl font-display text-[26px] leading-[1.15] font-medium tracking-[-0.01em] text-ink-700 md:text-[36px]"
        >
          {t('PROCESS.TITLE')}
        </h2>

        <ol className="mt-8 flex flex-col md:mt-12 md:grid md:grid-cols-4 md:gap-6">
          {STEP_KEYS.map((key, index) => (
            <li
              key={key}
              className={`flex items-start gap-4 py-5 md:flex-col md:gap-0 md:border-t-0 md:py-0 ${
                index > 0 ? 'border-t border-ink-100/15' : 'pt-0'
              }`}
            >
              <div className="relative size-24 shrink-0 overflow-hidden rounded-lg bg-blue-500/10 md:aspect-4/3 md:size-auto md:w-full">
                <Image
                  src={ON_DEMAND_STEP_IMAGES[index]}
                  alt=""
                  fill
                  sizes="(max-width: 899px) 96px, 25vw"
                  className="object-cover"
                  loading="lazy"
                />
              </div>
              <div className="flex min-w-0 flex-col gap-1.5 md:mt-5 md:gap-2">
                <h3 className="font-display text-[18px] leading-[1.2] font-medium text-ink-700 md:text-[20px]">
                  <span className="mr-2 font-display text-blue-500 italic">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  {t(`PROCESS.${key}.TITLE`)}
                </h3>
                <p className="text-[15px] leading-relaxed text-ink-200 md:text-base">
                  {t(`PROCESS.${key}.BODY`)}
                </p>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  )
}

export default React.memo(FourStepsProcess)
